import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Mic2, Users, Building, Rocket, Target, MessageSquare, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';

const speakingTopics = [
  {
    icon: Target,
    title: 'Digital Infrastructure Intelligence & Planning',
    description: 'How to transform fragmented infrastructure data into decision-ready intelligence for strategic planning.'
  },
  {
    icon: Rocket,
    title: 'Scalable Connectivity & FTTH Network Design',
    description: 'Building cost-efficient, future-ready fibre architectures for urban and underserved communities.'
  },
  {
    icon: Users,
    title: 'Data-Driven Decision Making',
    description: 'Implementing analytics platforms that enable resilience, efficiency, and measurable outcomes.'
  },
  {
    icon: Building,
    title: 'Emerging Market Digital Transformation',
    description: 'Navigating the unique challenges and opportunities of technology deployment across Africa.'
  },
  {
    icon: MessageSquare,
    title: 'Founder-Led System Design & Innovation',
    description: 'Building and scaling technology companies with limited resources and high impact goals.'
  }
];

const engagementTypes = [
  { type: 'Keynote Speaking', desc: 'Conference keynotes and industry events' },
  { type: 'Panel Discussions', desc: 'Expert panels on infrastructure and tech' },
  { type: 'Advisory Services', desc: 'Strategic guidance for organizations' },
  { type: 'Workshops', desc: 'Hands-on sessions for teams and leaders' }
];

const speakingGallery = [
  {
    src: 'https://customer-assets.emergentagent.com/job_profile-admin-hub/artifacts/5vdpsqyv_ME%201.jpg',
    alt: 'Ogaba Joseph Attah speaking at Koinonia School of Ministry',
    event: 'Koinonia School of Ministry'
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_profile-admin-hub/artifacts/6bwzkulg_ME%202.jpg',
    alt: 'Ogaba Joseph Attah presenting at leadership event',
    event: 'Leadership Conference'
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_profile-admin-hub/artifacts/xxji2x6n_ME%203.jpg',
    alt: 'Ogaba Joseph Attah keynote address',
    event: 'Keynote Address'
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_profile-admin-hub/artifacts/n0iag588_ME%204.jpg',
    alt: 'Ogaba Joseph Attah speaking engagement',
    event: 'Speaking Engagement'
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_profile-admin-hub/artifacts/19pkcu40_ME%20AND%20AUDIENCE.jpg',
    alt: 'Ogaba Joseph Attah with audience at Koinonia',
    event: 'Koinonia Ministry - Audience View'
  }
];

export default function SpeakingPage() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % speakingGallery.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + speakingGallery.length) % speakingGallery.length);
  };

  return (
    <>
      <SEOHead
        title="Ogaba Joseph Attah | Speaker & Digital Strategy Advisor"
        description="Ogaba Joseph Attah speaks and advises on digital infrastructure intelligence, scalable connectivity, data-driven decision-making, and emerging market digital transformation."
        canonicalUrl="https://www.ogabajosephattah.com/speaking-advisory"
      />
      
      <Navigation />
      
      <main className="pt-24 lg:pt-32">
        {/* Hero */}
        <section className="py-16 md:py-24" data-testid="speaking-hero">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-6">
                  Engagements
                </p>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[0.95] mb-6" data-testid="speaking-title">
                  Speaking & Advisory
                </h1>
                <p className="text-lg text-muted-foreground mb-8">
                  Ogaba Joseph Attah speaks and advises on digital infrastructure intelligence, scalable connectivity, data-driven decision-making, and emerging market digital transformation.
                </p>
                <p className="text-muted-foreground mb-8">
                  He works with conferences, public institutions, startups, and private organisations seeking clarity at the intersection of technology, infrastructure, and intelligence.
                </p>
                <Button asChild size="lg" data-testid="speaking-cta">
                  <Link to="/contact">
                    Request Speaking Engagement
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-muted">
                  <img
                    src="https://customer-assets.emergentagent.com/job_profile-admin-hub/artifacts/l7r1gzjw_One%20Community%20Team%20Headshot0173.jpg"
                    alt="Ogaba Joseph Attah - Speaking"
                    className="w-full h-full object-cover"
                    data-testid="speaking-image"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Speaking Topics */}
        <section className="py-24 md:py-32 bg-muted/30" data-testid="speaking-topics">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <div className="flex items-center gap-3 mb-4">
                <Mic2 className="h-5 w-5 text-primary" />
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
                  Topics
                </p>
              </div>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
                Speaking Topics
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {speakingTopics.map((topic, index) => (
                <motion.div
                  key={topic.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="p-6 bg-card border border-border rounded-xl hover:border-primary/30 transition-colors"
                  data-testid={`speaking-topic-${index}`}
                >
                  <div className="p-3 bg-primary/10 rounded-lg w-fit mb-4">
                    <topic.icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-2">{topic.title}</h3>
                  <p className="text-sm text-muted-foreground">
                    {topic.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Engagement Types */}
        <section className="py-24 md:py-32" data-testid="engagement-types">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-4">
                Services
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
                Types of Engagement
              </h2>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {engagementTypes.map((item, index) => (
                <motion.div
                  key={item.type}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="p-6 bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-xl text-center"
                  data-testid={`engagement-type-${index}`}
                >
                  <h3 className="font-semibold mb-2">{item.type}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Speaking Gallery */}
        <section className="py-24 md:py-32" data-testid="speaking-gallery">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-4">
                Gallery
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
                Speaking Engagements
              </h2>
              <p className="text-muted-foreground mt-4 max-w-2xl">
                Recent speaking engagements including sessions at Koinonia School of Ministry and Leadership conferences.
              </p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {speakingGallery.map((image, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group cursor-pointer"
                  onClick={() => openLightbox(index)}
                  data-testid={`gallery-image-${index}`}
                >
                  <div className="aspect-[3/4] rounded-xl overflow-hidden bg-muted">
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    {image.event}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Lightbox */}
        <AnimatePresence>
          {lightboxOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
              onClick={closeLightbox}
              data-testid="lightbox"
            >
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-4 right-4 text-white hover:bg-white/10 z-10"
                onClick={closeLightbox}
                data-testid="lightbox-close"
              >
                <X className="h-6 w-6" />
              </Button>
              
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 z-10"
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                data-testid="lightbox-prev"
              >
                <ChevronLeft className="h-8 w-8" />
              </Button>
              
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 z-10"
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                data-testid="lightbox-next"
              >
                <ChevronRight className="h-8 w-8" />
              </Button>

              <motion.div
                key={currentImageIndex}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                className="max-w-4xl max-h-[85vh] px-4"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={speakingGallery[currentImageIndex].src}
                  alt={speakingGallery[currentImageIndex].alt}
                  className="max-w-full max-h-[80vh] object-contain rounded-lg"
                />
                <p className="text-white text-center mt-4 text-sm">
                  {speakingGallery[currentImageIndex].event}
                </p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Advisory */}
        <section className="py-24 md:py-32 bg-muted/30" data-testid="advisory-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-4">
                  Advisory
                </p>
                <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">
                  Strategic Advisory Services
                </h2>
                <p className="text-muted-foreground mb-6">
                  Beyond speaking engagements, Ogaba provides strategic advisory services to organizations navigating digital infrastructure challenges, technology investments, and emerging market expansion.
                </p>
                <ul className="space-y-3 text-muted-foreground mb-8">
                  <li className="flex items-start gap-3">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary mt-2" />
                    <span>Infrastructure strategy and planning</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary mt-2" />
                    <span>Technology due diligence</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary mt-2" />
                    <span>Digital transformation roadmaps</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary mt-2" />
                    <span>Startup ecosystem guidance</span>
                  </li>
                </ul>
                <Button asChild data-testid="advisory-cta">
                  <Link to="/contact">
                    Discuss Advisory Needs
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="relative"
              >
                <div className="aspect-video rounded-2xl overflow-hidden bg-muted">
                  <img
                    src="https://customer-assets.emergentagent.com/job_682de1e5-c93e-434b-afcf-b22d5a4ba52c/artifacts/acqvwdfd_IMG-20241020-WA0049.jpg"
                    alt="Ogaba Joseph Attah - Advisory"
                    className="w-full h-full object-cover"
                    data-testid="advisory-image"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 md:py-32" data-testid="speaking-cta-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">
                Book a Speaking Engagement
              </h2>
              <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
                For conferences, corporate events, workshops, or advisory consultations.
              </p>
              <Button asChild size="lg" data-testid="book-speaking-cta">
                <Link to="/contact">
                  Get in Touch
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
